import type { DataSource } from '../../types/siteRag';
import type { DocFieldSource } from '../../types/siteEvidence';
import type { SourceRoleSed, SourceRoleTruthFinding } from './sourceRoleTruthGuard';

/** These checks bind an adopted source fact, not an arbitrary citation title.
 * Unknown applicability and edited values cannot inherit the original binding. */
export function adoptedWaterFieldSources(sed: SourceRoleSed): Array<{ field: string; source: DocFieldSource }> {
  const water = sed.waterResource;
  return Object.entries(water?.docFieldSources ?? {}).flatMap(([field, source]) => {
    const [group, leaf] = field.split('.');
    if (!['municipalSupply', 'wastewaterService'].includes(group ?? '') || !leaf) return [];
    const data = water?.data?.[group as 'municipalSupply' | 'wastewaterService'];
    if (!data || source.attribution?.appliesToProject === 'other-project' || !source.docId || !source.excerptPreview
      || data[leaf as keyof typeof data] !== source.value) return [];
    return [{ field, source }];
  });
}

const normalized = (text: string) => text.toLowerCase().replace(/(\d)\s*["″]/g, '$1 inch ')
  .replace(/(\d)-?\s*inches?\b/g, '$1 inch ').replace(/[^a-z0-9.]+/g, ' ').trim();
const documentId = (source: DataSource | undefined) => source?.documentId
  ?? (source?.url?.startsWith('doc://') ? source.url.slice(6) : undefined);

function repeatsSourceFact(text: string, field: string, source: DocFieldSource): boolean {
  if (field === 'wastewaterService.sourceDescription' && typeof source.value === 'string') {
    // Preserve the actual original dimensional/material identity across ordinary
    // quote/inch typography. Mere mention of a sewer does not acquire this fact.
    const dimension = normalized(source.value).match(/\b(\d+(?:\.\d+)?) inch polyethylene\b/)?.[0];
    return !!dimension && normalized(source.excerptPreview ?? '').includes(dimension)
      && normalized(text).includes(dimension) && /\b(?:sewer|main|pipe|forced?\s+pressure)\b/i.test(text);
  }
  return false;
}

export function checkSourceDocumentRoles(text: string, sed: SourceRoleSed, sectionId: string, references: DataSource[]): SourceRoleTruthFinding[] {
  const findings: SourceRoleTruthFinding[] = [];
  const sources = adoptedWaterFieldSources(sed);
  const otherDocs = new Set((sed.waterResource?.docSourceStatements ?? [])
    .filter(row => row.reason === 'other-project' || row.attribution?.appliesToProject === 'other-project').map(row => row.docId));
  let offset = 0;
  // A reference at the end may supply several preceding sentences. A reference
  // in an earlier statement cannot silently supply a subsequent different fact.
  const spans: Array<{ text: string; ids: string[] }> = [];
  for (const match of text.matchAll(/\[Ref\s+([\d,\s]+)\]/gi)) {
    const referenceEnd = match.index! + match[0].length;
    // Keep terminal punctuation with the referenced assertion: a citation
    // before '?' must not turn an interrogative into an affirmative fact.
    const end = referenceEnd + (text.slice(referenceEnd).match(/^[ \t]*[.!?]/)?.[0].length ?? 0);
    const ids = match[1]!.split(/[,\s]+/).filter(Boolean)
      .map(index => documentId(references[Number(index) - 1])).filter((id): id is string => !!id);
    // Adjacent reference tokens share the preceding claim.
    if (spans.length && /^[\s,;.]*$/.test(text.slice(offset, match.index))) {
      const prior = spans[spans.length - 1]!; prior.ids.push(...ids); prior.text += text.slice(offset, end);
    } else spans.push({ text: text.slice(offset, end), ids });
    offset = end;
  }
  if (text.slice(offset).trim()) spans.push({ text: text.slice(offset), ids: [] });
  for (const { text: spanText, ids } of spans) {
    for (const excerpt of spanText.split(/(?<=[.!?;])\s+|,\s*(?=(?:but|however|while|whereas)\b)/i)) {
      // A question asking whether a main exists does not assert the adopted fact.
      if (excerpt.trim().endsWith('?') && !/\b(?:given|because|since)\b/i.test(excerpt)) continue;
      if (/\b(?:other|another|different)\s+project\b/i.test(excerpt)
        && !/\b(?:this|our)\s+(?:site|project)\b/i.test(excerpt)) continue;
      // Negation is local to this assertion; a later affirmative sentence or
      // contrasting clause must still prove its own source identity.
      if (/\b(?:do(?:es)?\s+not|cannot|can't)\s+(?:confirm|establish|verify|demonstrate|prove|document)\b/i.test(excerpt)) continue;
      for (const { field, source } of sources) {
        if (!repeatsSourceFact(excerpt, field, source)) continue;
        if (ids.includes(source.docId) && !ids.some(id => otherDocs.has(id))) continue;
        findings.push({ kind: 'source-document-citation', severity: 'block', sectionId, excerpt: excerpt.trim(), observed: excerpt.trim(),
          expected: `This adopted ${field} fact is supplied by ${source.docName} (document ${source.docId}). Cite that exact source for this statement; a different project's citation or a nearby operating-rate citation cannot supply it.`,
          message: 'An adopted source fact does not cite its supplying document, or mixes its site applicability with an explicitly other-project source.',
          sourcePaths: [`waterResource.docFieldSources.${field}`], documentIds: [source.docId] });
      }
    }
  }

  const sourceDescription = sources.find(row => row.field === 'municipalSupply.sourceDescription');
  if (sourceDescription && typeof sourceDescription.source.value === 'string') {
    const namedAquifer = sourceDescription.source.value.match(/\b(?:from\s+(?:the\s+)?)?([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*\s+Aquifer)\b/)?.[1];
    if (namedAquifer) for (const excerpt of text.split(/(?<=[.!?])\s+|\n+/)) {
      if (!excerpt.toLowerCase().includes(namedAquifer.toLowerCase())) continue;
      const deniesRecordedSource = /\b(?:this\s+is|(?:named\b.{0,80}\bsource|these\s+figures\b.{0,100}\bsource)\s+(?:is|are))\s+not\s+(?:confirmed|verified|recorded|documented)\s+in\s+(?:the\s+)?(?:provided\s+)?SED\b/i.test(excerpt);
      if (deniesRecordedSource) findings.push({ kind: 'recorded-source-absence', severity: 'block', sectionId, excerpt, observed: excerpt,
        expected: `The frozen SED records municipal source context: ${sourceDescription.source.value}. Preserve the document attribution; site well yield, hydrogeology, allocation and rights may still be unverified.`,
        message: 'The report calls documented municipal source context absent from the SED. Recorded source identity is distinct from verified site supply.',
        sourcePaths: ['waterResource.docFieldSources.municipalSupply.sourceDescription'], documentIds: [sourceDescription.source.docId] });
    }
  }
  return findings;
}
